import { Toggle } from '@moonpig/launchpad-forms'
import { Box, Flex, Text } from '@moonpig/launchpad-components'
import { IconSystemCardAdd } from '@moonpig/launchpad-assets'
import React, { useEffect, useState } from 'react'
import { styled } from '@moonpig/launchpad-utils'
import { useSearchAnalytics } from '../../pages/SearchPage/analytics'
import { multiBuyEvent } from '../../pages/SearchPage/events'
import { useFindLocaleText } from '../../text-localisation'
import {
  CookieCategory,
  getBrowserCookie,
  setBrowserCookie,
} from '@moonpig/web-core-browser-storage'
import { MULTI_BUY_COOKIE_NAME } from '../../constants'
import { useSearchStore } from '../../store/SearchStore'
import {
  trackGAEvent,
  createGenericSelectContentEvent,
  createGenericImpressionEvent,
  useTrackGAEventOnce,
} from '@moonpig/web-core-analytics'
import { PageTypeEnum } from '@moonpig/web-shared-products'

const MULTI_BUY_TOGGLE_ID = 'multi-buy-toggle'

const StyledLabel = styled(Flex)`
  cursor: pointer;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  padding: 5px 5px;
  border: 2px solid ${({ theme }) => theme.colors.colorBlack20};
  margin: 4px 0;
  gap: 8px;
`

const StyledIconSystemCardAdd = styled(IconSystemCardAdd)`
  margin-right: 8px;
  margin-top: auto;
  margin-bottom: auto;
`

export const MultiBuyToggle = ({
  trackingCategory,
}: {
  trackingCategory: PageTypeEnum.GALLERY | PageTypeEnum.SEARCH
}) => {
  const isLaunchpadAnalytics = useSearchAnalytics()
  const localiseText = useFindLocaleText()
  const { trackGAEventOnce } = useTrackGAEventOnce()
  const [isMultiBuyEnabled, setIsMultiBuyEnabled] = useState(false)
  const toggleMultiBuyEnabled = useSearchStore(
    store => store.toggleMultiBuyEnabled,
  )

  useEffect(() => {
    const multiBuyCookieState =
      getBrowserCookie(MULTI_BUY_COOKIE_NAME) === 'true'

    setIsMultiBuyEnabled(multiBuyCookieState)
    toggleMultiBuyEnabled(multiBuyCookieState)
  }, [toggleMultiBuyEnabled])

  useEffect(() => {
    const multiBuyCookieState =
      getBrowserCookie(MULTI_BUY_COOKIE_NAME) === 'true'
    trackGAEventOnce(
      createGenericImpressionEvent({
        category: trackingCategory,
        action: 'view multibuy toggle',
        label: `multibuy toggle | ${multiBuyCookieState ? 'enabled' : 'default'}`,
      }),
    )
  }, [trackGAEventOnce, trackingCategory])

  const handleMultiBuyEnabled = (isEnabled: boolean) => {
    setIsMultiBuyEnabled(!isEnabled)
    setBrowserCookie(
      MULTI_BUY_COOKIE_NAME,
      JSON.stringify(!isEnabled),
      { path: '/' },
      CookieCategory.STRICTLY_NECESSARY,
    )
    toggleMultiBuyEnabled(!isEnabled)
    // Launchpad event: apps/explore/find/src/pages/SearchPage/events.ts
    if (!isLaunchpadAnalytics) {
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: trackingCategory,
          eventAction: !isEnabled
            ? 'enable multibuy toggle'
            : 'disable multibuy toggle',
          eventLabel: !isEnabled
            ? 'multibuy toggle | enable'
            : 'multibuy toggle | disable',
        }),
      )
    }
  }

  const toggleAction = isMultiBuyEnabled
    ? 'disable multibuy toggle'
    : 'enable multibuy toggle'
  const toggleLabel = isMultiBuyEnabled
    ? 'multibuy toggle | disable'
    : 'multibuy toggle | enable'
  // Legacy event: apps/explore/find/src/components/MultiBuyToggle/MultiBuyToggle.tsx
  const multiBuyChangeEvent = multiBuyEvent()
    .withEventData({
      category: trackingCategory,
      action: toggleAction,
      label: toggleLabel,
    })
    .withContentData({
      content_type: `${trackingCategory} | ${toggleAction} | ${toggleLabel}`,
    })

  return (
    <StyledLabel as="label" htmlFor={MULTI_BUY_TOGGLE_ID}>
      <Flex>
        <StyledIconSystemCardAdd width={24} height={24} />
        <Flex flexDirection="column">
          <Text id="multi-buy-toggle-label" typography="typeBodyLabel">
            {localiseText('find.multi_buy_toggle.label')}
          </Text>
          <Text id="multi-buy-toggle-description" typography="typeBodyText">
            {localiseText('find.multi_buy_toggle.description')}
          </Text>
        </Flex>
      </Flex>
      <Box style={{ scale: 0.8, transformOrigin: 'right' }}>
        <Toggle
          events={
            isLaunchpadAnalytics ? { onChange: multiBuyChangeEvent } : undefined
          }
          onChange={() => {
            handleMultiBuyEnabled(isMultiBuyEnabled)
          }}
          value={MULTI_BUY_TOGGLE_ID}
          name={MULTI_BUY_TOGGLE_ID}
          id={MULTI_BUY_TOGGLE_ID}
          checked={isMultiBuyEnabled}
          aria-labelledby="multi-buy-toggle-label"
          aria-describedby="multi-buy-toggle-description"
        />
      </Box>
    </StyledLabel>
  )
}
